import type { BuilderAnswerType } from '@/api/questionnaires/types';
import { BUILDER_ANSWER_TYPES } from '../-types';

/** Display metadata for every answer type the builder can insert.
 *
 *  - Drives the Question Library's Custom group.
 *  - The first five entries are the v2-native Pascal-case types; the
 *    remaining six keep the legacy ALL_CAPS keys so the response page
 *    on the old frontend renders them correctly.
 *  - Order matches the old frontend's Custom section. */
export interface AnswerTypeMeta {
  type: BuilderAnswerType;
  /** Translation key under `builder.answerTypes.*`. */
  labelKey: string;
  /** Font Awesome class. */
  icon: string;
}

export const ANSWER_TYPE_CATALOG: AnswerTypeMeta[] = [
  {
    type: BUILDER_ANSWER_TYPES.SHORT_TEXT,
    labelKey: 'builder.answerTypes.shortText',
    icon: 'fa-light fa-input-text',
  },
  {
    type: BUILDER_ANSWER_TYPES.LONG_TEXT,
    labelKey: 'builder.answerTypes.longText',
    icon: 'fa-light fa-align-left',
  },
  {
    type: BUILDER_ANSWER_TYPES.NUMBER,
    labelKey: 'builder.answerTypes.number',
    icon: 'fa-light fa-hashtag',
  },
  {
    type: BUILDER_ANSWER_TYPES.YES_OR_NO,
    labelKey: 'builder.answerTypes.yesOrNo',
    icon: 'fa-light fa-circle-check',
  },
  {
    type: BUILDER_ANSWER_TYPES.SELECT,
    labelKey: 'builder.answerTypes.select',
    icon: 'fa-light fa-list',
  },
  {
    type: BUILDER_ANSWER_TYPES.NUMBER_UOM,
    labelKey: 'builder.answerTypes.numberUom',
    icon: 'fa-light fa-ruler-combined',
  },
  {
    type: BUILDER_ANSWER_TYPES.CHECK_BOXES,
    labelKey: 'builder.answerTypes.multipleChoice',
    icon: 'fa-light fa-list-check',
  },
  {
    type: BUILDER_ANSWER_TYPES.DATE,
    labelKey: 'builder.answerTypes.date',
    icon: 'fa-light fa-calendar',
  },
  {
    type: BUILDER_ANSWER_TYPES.DOCUMENTS,
    labelKey: 'builder.answerTypes.documents',
    icon: 'fa-light fa-file-lines',
  },
  {
    type: BUILDER_ANSWER_TYPES.AUDIT,
    labelKey: 'builder.answerTypes.audit',
    icon: 'fa-light fa-clipboard-check',
  },
  {
    type: BUILDER_ANSWER_TYPES.CERTIFICATE,
    labelKey: 'builder.answerTypes.certificate',
    icon: 'fa-light fa-certificate',
  },
];

/** Quick lookup from type to its translation key. Used by the question
 *  card to render the "Type: ..." pill.
 *
 *  When `isTemplateBound` is true (the question is sourced from a
 *  Retraced template via `retracedDatapointId`) and the answer type
 *  isn't one of the user-selectable catalog entries, returns
 *  `builder.answerTypes.template` — the template owns the answer
 *  shape, so "Template" reads better than "Unknown type". */
export function answerTypeLabelKey(
  type: string,
  isTemplateBound = false,
): string {
  const meta = ANSWER_TYPE_CATALOG.find(m => m.type === type);
  if (meta) return meta.labelKey;
  return isTemplateBound
    ? 'builder.answerTypes.template'
    : 'builder.answerTypes.unknown';
}

/** Quick lookup from type to its Font Awesome icon class. */
export function answerTypeIcon(type: string): string {
  const meta = ANSWER_TYPE_CATALOG.find(m => m.type === type);
  return meta?.icon ?? 'fa-light fa-circle-question';
}

/** Maps the server's `fieldTypeId` strings (returned by
 *  `data-points?groupByTemplates=true`) to our BuilderAnswerType. Types
 *  not in this map are dropped from the library. */
export const FIELD_TYPE_TO_ANSWER_TYPE: Record<string, BuilderAnswerType> = {
  // Text-like
  SHORT_TEXT: BUILDER_ANSWER_TYPES.SHORT_TEXT,
  STRING: BUILDER_ANSWER_TYPES.SHORT_TEXT,
  ADDRESS: BUILDER_ANSWER_TYPES.SHORT_TEXT,
  CUSTOM_IDENTIFIER: BUILDER_ANSWER_TYPES.SHORT_TEXT,
  LONG_TEXT: BUILDER_ANSWER_TYPES.LONG_TEXT,
  // Multi-row dynamic table — the preview renders a real cell-edited
  // mini-table driven by the datapoint's `meta.columns`.
  TABLE: BUILDER_ANSWER_TYPES.TABLE,

  // Boolean-like
  YES_NO: BUILDER_ANSWER_TYPES.YES_OR_NO,
  BOOLEAN: BUILDER_ANSWER_TYPES.YES_OR_NO,
  TOGGLE: BUILDER_ANSWER_TYPES.YES_OR_NO,
  CHECK_BOX: BUILDER_ANSWER_TYPES.YES_OR_NO,

  // Numbers
  NUMBER: BUILDER_ANSWER_TYPES.NUMBER,
  // Legacy currency / year-bearing number variants degrade to plain
  // number-with-unit for now — the builder doesn't expose those types
  // yet but old questionnaires can still load.
  NUMBER_UOM: BUILDER_ANSWER_TYPES.NUMBER_UOM,
  NUMBER_DYNAMIC_UOM: BUILDER_ANSWER_TYPES.NUMBER_UOM,
  NUMBER_DYNAMIC_CURRENCY: BUILDER_ANSWER_TYPES.NUMBER_UOM,
  NUMBER_DYNAMIC_UOM_WITH_YEAR: BUILDER_ANSWER_TYPES.NUMBER_UOM,
  NUMBER_DYNAMIC_CURRENCY_WITH_YEAR: BUILDER_ANSWER_TYPES.NUMBER_UOM,

  // Dates
  DATE: BUILDER_ANSWER_TYPES.DATE,
  SELECT_YEAR: BUILDER_ANSWER_TYPES.DATE,

  // Choice (single-pick)
  SELECT: BUILDER_ANSWER_TYPES.SELECT,
  SELECT_WITH_FOLLOW_UP: BUILDER_ANSWER_TYPES.SELECT,

  // Choice (multi-pick) — every multi-select variant collapses onto the
  // checkbox preview.
  CHECK_BOXES: BUILDER_ANSWER_TYPES.CHECK_BOXES,
  MULTIPLE_CHECK_BOXES: BUILDER_ANSWER_TYPES.CHECK_BOXES,
  MULTIPLE_CHOICE: BUILDER_ANSWER_TYPES.CHECK_BOXES,
  MULTIPLE_SELECT: BUILDER_ANSWER_TYPES.CHECK_BOXES,
  MULTI_SELECT: BUILDER_ANSWER_TYPES.CHECK_BOXES,

  // File / asset uploads
  DOCUMENTS: BUILDER_ANSWER_TYPES.DOCUMENTS,
  FILE_UPLOAD: BUILDER_ANSWER_TYPES.DOCUMENTS,
  // A plain image upload — NOT a company-document/asset link. Routes to the
  // image-upload editor in the responder instead of the asset-link selector.
  MULTIPLE_PHOTO_UPLOAD: BUILDER_ANSWER_TYPES.PHOTO_UPLOAD,
  // Single video upload — NOT a company-document/asset link. Routes to the
  // video-upload editor in the responder instead of the asset-link selector.
  VIDEO: BUILDER_ANSWER_TYPES.VIDEO,
  VIDEO_UPLOAD: BUILDER_ANSWER_TYPES.VIDEO,
  AUDIT: BUILDER_ANSWER_TYPES.AUDIT,
  CERTIFICATE: BUILDER_ANSWER_TYPES.CERTIFICATE,
};
